import {
  ReactNode,
  createContext,
  useContext,
  useCallback,
  useMemo,
  useEffect,
  useRef,
} from 'react'
import { useStytchB2BClient, useStytchMemberSession } from '@stytch/react/b2b'
import { useQueryClient } from '@tanstack/react-query'
import { useGetAccounts } from '@/service-library/hooks/accounts'
import { useExchangeToken } from '@/service-library/hooks/auth'
import { useGetOrganizations } from '@/service-library/hooks/organizations'
import { Account } from '@/types/accounts'
import { Organization } from '@/types/organizations'
import { showErrorSnackbar } from '@/utils/snackbars'

type CurrentAccountContextType = {
  currentAccount: Account
  setCurrentAccount: (accountId: string) => Promise<void>
  rootOrganization: Organization
  rootOrganizations: Organization[]
} & ReturnType<typeof useGetAccounts>

const CurrentAccountContext = createContext<CurrentAccountContextType>(
  {} as CurrentAccountContextType,
)

type CurrentAccountProviderProps = {
  children: ReactNode
}

export const useCurrentAccountContext = () => useContext(CurrentAccountContext)

export default function CurrentAccountProvider({
  children,
}: CurrentAccountProviderProps) {
  const queryClient = useQueryClient()
  const { session } = useStytchMemberSession()
  const stytch = useStytchB2BClient()

  const accountsQuery = useGetAccounts({
    refetchOnWindowFocus: false,
    filters: {
      limit: '1000',
      is_archived: 'false',
    },
  })
  const { accounts, queryKey: accountsQueryKey } = accountsQuery

  const { exchangeTokenAsync } = useExchangeToken({
    onError: () => {
      showErrorSnackbar('Failed to switch account. Please try again later.')
    },
  })

  const currentAccount = useMemo(() => {
    return accounts.find(
      (account) => account.id === session?.custom_claims?.['pd-account_id'],
    )
  }, [accounts, session?.custom_claims])

  const { organizations } = useGetOrganizations({
    refetchOnWindowFocus: false,
    filters: {
      parent_org_id__isnull: 'true',
    },
    enabled: !!accounts.length,
  })

  const rootOrganization = useMemo(
    () =>
      organizations.find(({ account_id }) => currentAccount?.id === account_id),
    [currentAccount?.id, organizations],
  )

  const setCurrentAccount = useCallback(
    async (accountId: string) => {
      const { session_token, session_jwt } = await exchangeTokenAsync({
        to_type: 'account_id',
        to: accountId,
      })

      stytch.session.updateSession({
        session_token:
          session_token || stytch.session.getTokens()?.session_token || '',
        session_jwt,
      })
      await stytch.session.authenticate({ session_duration_minutes: 60 })
    },
    [exchangeTokenAsync, stytch.session],
  )

  const accountIdRef = useRef<string | undefined>(currentAccount?.id)
  useEffect(() => {
    if (
      currentAccount?.id &&
      accountIdRef.current &&
      accountIdRef.current !== currentAccount?.id
    ) {
      queryClient.resetQueries({
        predicate: (query) => {
          const keyToCompare = query.queryKey
          return (
            JSON.stringify(keyToCompare) !== JSON.stringify(accountsQueryKey)
          )
        },
      })
    }
    accountIdRef.current = currentAccount?.id
  }, [accountsQueryKey, currentAccount?.id, queryClient])

  if (!session) return children

  return currentAccount?.id && rootOrganization?.id ? (
    <CurrentAccountContext.Provider
      value={{
        ...accountsQuery,
        currentAccount,
        rootOrganization,
        rootOrganizations: organizations,
        setCurrentAccount,
      }}
    >
      {children}
    </CurrentAccountContext.Provider>
  ) : null
}
